import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, MapPin } from "lucide-react";

const AddressModal = ({ isOpen, onClose, onSave, editingAddress = null }) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "home",
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "India",
    phone: "",
  });

  const [errors, setErrors] = useState({});
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const stateRef = useRef(null);

  const indianStates = [
    { value: "", label: "Select State" },
    { value: "Andaman and Nicobar Islands", label: "Andaman and Nicobar Islands" },
    { value: "Andhra Pradesh", label: "Andhra Pradesh" },
    { value: "Arunachal Pradesh", label: "Arunachal Pradesh" },
    { value: "Assam", label: "Assam" },
    { value: "Bihar", label: "Bihar" },
    { value: "Chandigarh", label: "Chandigarh" },
    { value: "Chhattisgarh", label: "Chhattisgarh" },
    { value: "Dadra and Nagar Haveli and Daman and Diu", label: "Dadra and Nagar Haveli and Daman and Diu" },
    { value: "Delhi", label: "Delhi" },
    { value: "Goa", label: "Goa" },
    { value: "Gujarat", label: "Gujarat" },
    { value: "Haryana", label: "Haryana" },
    { value: "Himachal Pradesh", label: "Himachal Pradesh" },
    { value: "Jammu and Kashmir", label: "Jammu and Kashmir" },
    { value: "Jharkhand", label: "Jharkhand" },
    { value: "Karnataka", label: "Karnataka" },
    { value: "Kerala", label: "Kerala" },
    { value: "Ladakh", label: "Ladakh" },
    { value: "Lakshadweep", label: "Lakshadweep" },
    { value: "Madhya Pradesh", label: "Madhya Pradesh" },
    { value: "Maharashtra", label: "Maharashtra" },
    { value: "Manipur", label: "Manipur" },
    { value: "Meghalaya", label: "Meghalaya" },
    { value: "Mizoram", label: "Mizoram" },
    { value: "Nagaland", label: "Nagaland" },
    { value: "Odisha", label: "Odisha" },
    { value: "Puducherry", label: "Puducherry" },
    { value: "Punjab", label: "Punjab" },
    { value: "Rajasthan", label: "Rajasthan" },
    { value: "Sikkim", label: "Sikkim" },
    { value: "Tamil Nadu", label: "Tamil Nadu" },
    { value: "Telangana", label: "Telangana" },
    { value: "Tripura", label: "Tripura" },
    { value: "Uttar Pradesh", label: "Uttar Pradesh" },
    { value: "Uttarakhand", label: "Uttarakhand" },
    { value: "West Bengal", label: "West Bengal" }
  ];

  
  useEffect(() => {
    if (editingAddress) {
      setFormData({
        name: editingAddress.address || "",
        type: editingAddress.type?.toLowerCase() || "home",
        street: editingAddress.street || "",
        city: editingAddress.city || "",
        state: editingAddress.state || "",
        zipCode: editingAddress.zipcode || "",
        country: editingAddress.country_id || "India",
        phone: editingAddress.phone || "",
      });
    } else {
      setFormData({
        name: "",
        type: "home",
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "India",
        phone: "",
      });
    }
    setErrors({});
  }, [editingAddress, isOpen]);

  const handleInputChange = (field, value) => {
    // Allow only alphabets and spaces for name and city fields
    if (field === "name" || field === "city") {
      value = value.replace(/[^a-zA-Z\s]/g, ""); // remove anything that's not a letter or space
      // Remove multiple consecutive spaces and trim
      value = value.replace(/\s+/g, " ");
    }

    // Allow only numbers for zipCode and phone
    if (field === "zipCode") {
      value = value.replace(/\D/g, ""); // remove non-digits
      // Limit to 6 digits for Indian PIN code
      if (value.length > 6) {
        value = value.slice(0, 6);
      }
    }

    if (field === "phone") {
      value = value.replace(/\D/g, ""); // remove non-digits
      // Limit to 10 digits for Indian mobile number
      if (value.length > 10) {
        value = value.slice(0, 10);
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      newErrors.name = "Name can only contain letters and spaces";
    }

    if (!formData.street.trim())
      newErrors.street = "Street address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.state.trim()) newErrors.state = "State is required";

    // Indian PIN code validation (exactly 6 digits)
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = "PIN code is required";
    } else if (!/^\d{6}$/.test(formData.zipCode)) {
      newErrors.zipCode = "PIN code must be exactly 6 digits";
    }

    // Indian mobile number validation (10 digits, starting with 6-9) - Now Required
    if (!formData.phone.trim()) {
      newErrors.phone = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Mobile number must be exactly 10 digits";
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = "Mobile number must start with 6, 7, 8, or 9";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      onSave({
        ...formData,
        name: formData.name.trim(),
        city: formData.city.trim(),
        id: editingAddress?.id || Date.now().toString(),
      });
      onClose();
    }
  };

  const toggleStateDropdown = useCallback(() => {
    setIsStateOpen((prev) => {
      const newState = !prev;
      // Only reset search when opening the dropdown
      if (newState) {
        setStateSearch("");
      }
      return newState;
    });
  }, []);

  const selectState = useCallback(
    (value) => {
      handleInputChange("state", value);
      setStateSearch("");
      setIsStateOpen(false);
    },
    [handleInputChange],
  );

  const handleStateItemClick = useCallback(
    (value, event) => {
      event.preventDefault();
      event.stopPropagation();
      selectState(value);
    },
    [selectState],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (stateRef.current && !stateRef.current.contains(event.target)) {
        setIsStateOpen(false);
      }
    };

    if (isStateOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isStateOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isStateOpen) {
        setIsStateOpen(false);
      }
    };
    if (isStateOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isStateOpen]);

  // Close dropdown when formData.state changes externally
  useEffect(() => {
    setIsStateOpen(false);
  }, [editingAddress]);
  useEffect(() => {
  // Close dropdown when formData.state changes externally
  setIsStateOpen(false);
}, [editingAddress]);

// ADD THIS HERE
useEffect(() => {
  if (isOpen) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "unset";
  }

  return () => {
    document.body.style.overflow = "unset";
  };
}, [isOpen]);


  if (!isOpen) return null;

  const filteredStates = indianStates.filter((state) =>
    state.label.toLowerCase().includes(stateSearch.toLowerCase())
  );
  
  return (
    <div
  className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm overflow-y-auto"
  role="dialog"
  aria-modal="true"
>
  <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto overscroll-contain">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2
            id="address-modal-title"
            className="text-xl font-semibold text-gray-900 flex items-center gap-2"
          >
            <MapPin className="w-5 h-5 text-teal-600" aria-hidden="true" />
            {editingAddress ? "Edit Address" : "Add New Address"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name and Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="address-name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Full Name *
              </label>
              <input
                id="address-name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., Rajesh Kumar"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                aria-describedby={errors.name ? "name-error" : undefined}
              />
              {errors.name && (
                <p
                  id="name-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.name}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Only letters and spaces allowed
              </p>
            </div>

            <div>
              <label
                htmlFor="address-type"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Address Type
              </label>
              <select
                id="address-type"
                value={formData.type}
                onChange={(e) => handleInputChange("type", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="home">Home</option>
                <option value="work">Work</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Street Address */}
          <div>
            <label
              htmlFor="street-address"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              House No., Building, Street, Area *
            </label>
            <input
              id="street-address"
              type="text"
              value={formData.street}
              onChange={(e) => handleInputChange("street", e.target.value)}
              placeholder="e.g., 123, ABC Apartments, MG Road"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                errors.street ? "border-red-500" : "border-gray-300"
              }`}
              aria-describedby={errors.street ? "street-error" : undefined}
            />
            {errors.street && (
              <p
                id="street-error"
                className="text-red-500 text-xs mt-1"
                role="alert"
              >
                {errors.street}
              </p>
            )}
          </div>

          {/* City */}
          <div>
            <label
              htmlFor="city"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              City *
            </label>
            <input
              id="city"
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
              placeholder="e.g., Mumbai"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                errors.city ? "border-red-500" : "border-gray-300"
              }`}
              aria-describedby={errors.city ? "city-error" : undefined}
            />
            {errors.city && (
              <p
                id="city-error"
                className="text-red-500 text-xs mt-1"
                role="alert"
              >
                {errors.city}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Only letters and spaces allowed
            </p>
          </div>

          {/* State and PIN Code */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="state"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                State *
              </label>
              <div className="relative" ref={stateRef}>
                <button
                  type="button"
                  id="state"
                  onClick={toggleStateDropdown}
                  className={`w-full px-3 py-2 border rounded-lg text-left flex items-center justify-between focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                    errors.state ? "border-red-500" : "border-gray-300"
                  } ${isStateOpen ? "ring-2 ring-teal-500 border-teal-500" : ""}`}
                  aria-haspopup="listbox"
                  aria-expanded={isStateOpen}
                  aria-describedby={errors.state ? "state-error" : undefined}
                >
                  {formData.state
                    ? indianStates.find(
                        (state) => state.value === formData.state,
                      )?.label || formData.state
                    : "Select State"}
                  <svg
                    className={`w-4 h-4 transition-transform ${isStateOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isStateOpen && (
                  <ul
                    className="absolute z-20 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-44 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 focus:outline-none"
                    role="listbox"
                    aria-activedescendant=""
                  >
                    <li className="px-3 py-2">
                      <input
                        type="text"
                        value={stateSearch}
                        onChange={(e) => setStateSearch(e.target.value)}
                        placeholder="Search state..."
                        className="w-full px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-teal-500 focus:border-teal-500 text-sm"
                        autoFocus
                      />
                    </li>
                    {filteredStates.map((state) => (
                      <li
                        key={state.value}
                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 focus:bg-gray-100 text-sm ${
                          formData.state === state.value
                            ? "bg-teal-100 text-teal-800 font-medium"
                            : "text-gray-900"
                        }`}
                        onClick={(e) => handleStateItemClick(state.value, e)}
                        role="option"
                        aria-selected={formData.state === state.value}
                      >
                        {state.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {errors.state && (
                <p
                  id="state-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.state}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="zip-code"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                PIN Code *
              </label>
              <input
                id="zip-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="6"
                value={formData.zipCode}
                onChange={(e) => handleInputChange("zipCode", e.target.value)}
                placeholder="400001"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  errors.zipCode ? "border-red-500" : "border-gray-300"
                }`}
                aria-describedby={errors.zipCode ? "zip-error" : undefined}
              />
              {errors.zipCode && (
                <p
                  id="zip-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.zipCode}
                </p>
              )}
            </div>
          </div>

          {/* Phone Number */}
          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Mobile Number *
            </label>
            <input
              id="phone"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="10"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              placeholder="9876543210"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                errors.phone ? "border-red-500" : "border-gray-300"
              }`}
              aria-describedby={errors.phone ? "phone-error" : undefined}
            />
            {errors.phone && (
              <p
                id="phone-error"
                className="text-red-500 text-xs mt-1"
                role="alert"
              >
                {errors.phone}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Enter 10-digit mobile number starting with 6, 7, 8, or 9
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-teal-800 text-white rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
            >
              {editingAddress ? "Update Address" : "Add Address"}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  );
};

export default AddressModal;
